// PanCardForm.tsx

import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import Footer from "../../../core/common/footer/Footer";
import bulbImage from "../../../images/buld.png";
import useApi from "../../../core/customhook/useApi";
import z from "zod";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../core/data/redux/store";
import SnipperLoader from "../../../core/common/loader/SnipperLoader";
import { triggerPackageReload } from "../../../core/data/redux/packageSlice";
import { apibaseurl, REACT_APP_SUREPASS_API_KEY } from "../../../environment";
import { all_routes } from "../../router/all_routes";
import { useLocation, useNavigate } from "react-router";
import { useAuth } from "../../auth/AuthContext";
import { disabledStatuses } from "../../../core/common/DateValidHelper";

const PanCardWithoutValid = () => {
  const templates = useSelector((state: RootState) => state.package.templates);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { authData } = useAuth();

  const { state } = useLocation();

  const {
    assignment_template_id,
    assignment_id,
    case_id,
    candidate_id,
    actor_type,
  } = state || {};

  const panTemplate = templates.find(
    (item: any) =>
      item.check_name === "PAN card verification (without validation)",
  );

  const assignmentTemplateId = panTemplate?.assignment_template_id;
  const caseId = panTemplate?.case_id;
  const assignmentId = panTemplate?.assignment_id;
  const candidateId = authData?.candidate?.candidateId;
  const templateStatus = panTemplate?.status;

  const [formFields, setFormFields] = useState<any[]>([]);
  const [formData, setFormData] = useState<any>({});
  const [noDocument, setNoDocument] = useState<Record<string, boolean>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // ======= Modal State =============

  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyRemarks, setReplyRemarks] = useState("");
  const [replyDocuments, setReplyDocuments] = useState<File[]>([]);

  // ======== Modal stat eend her e===============

  const generateSchema = (
    fields: any[],
    noDocument: Record<string, boolean>,
  ) => {
    const schemaFields: Record<string, z.ZodTypeAny> = {};

    fields.forEach((field) => {
      let validator: z.ZodTypeAny = z.any();

      // Text fields
      if (field.type !== "file") {
        let stringValidator: z.ZodString = z.string();

        if (field.required) {
          stringValidator = stringValidator.min(
            1,
            `${field.label} is required`,
          );
        }

        if (field.validation_rules?.minLength) {
          stringValidator = stringValidator.min(
            field.validation_rules.minLength,
            `${field.label} must be at least ${field.validation_rules.minLength} characters`,
          );
        }

        if (field.validation_rules?.maxLength) {
          stringValidator = stringValidator.max(
            field.validation_rules.maxLength,
            `${field.label} cannot exceed ${field.validation_rules.maxLength} characters`,
          );
        }

        if (field.validation_rules?.pattern) {
          stringValidator = stringValidator.regex(
            new RegExp(field.validation_rules.pattern),
            `${field.label} is invalid`,
          );
        }

        validator = field.required ? stringValidator : z.string().optional();
      }

      // File fields
      if (field.type === "file") {
        const skipValidation = noDocument?.[field.field_id] === true;

        if (skipValidation) {
          validator = z.any().optional();
        } else {
          validator = field.required
            ? z.instanceof(File, {
                message: `${field.label} is required`,
              })
            : z.any().optional();
        }
      }

      schemaFields[field.field_id] = validator;
    });

    return z.object(schemaFields);
  };

  const formFieldsAPI = useApi<any>(
    `/api/v1/candidate/templates/${assignmentTemplateId}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const templateSaveAPI = useApi<any>(
    `/api/v1/candidate/templates/${assignmentTemplateId}/save`,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const templateUpdateAPI = useApi<any>(
    `/api/v1/candidate/forms/filled/${assignmentTemplateId}`,
    {
      method: "PUT",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const insuffResponse = useApi<any>(
    `/api/v1/insufficiency/candidate-response`,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const panCardForm = async () => {
    try {
      const response = await formFieldsAPI.callApi();

      if (response?.success) {
        const fields = response?.data?.fields || [];

        setFormFields(fields);

        const initialValues: any = {};

        fields.forEach((field: any) => {
          initialValues[field.field_id] =
            field.type === "file" ? null : field.value || "";
        });

        const userPersonalData = JSON.parse(
          localStorage.getItem("userPersonalData") || "{}",
        );

        const fullName = [
          userPersonalData?.first_name,
          userPersonalData?.middle_name,
          userPersonalData?.last_name,
        ]
          .filter(Boolean)
          .join(" ");

        if (!initialValues.applicant_name && fullName) {
          initialValues.applicant_name = fullName;
        }

        initialValues.date_of_birth =
          initialValues.date_of_birth || userPersonalData?.dob
            ? new Date(userPersonalData?.dob).toISOString().split("T")[0]
            : "";

        initialValues.parent_name =
          initialValues.parent_name ||
          userPersonalData?.father_or_mother_name ||
          "";
        initialValues.pan_number =
          initialValues.pan_number || userPersonalData?.pan_no || "";

        setFormData(initialValues);
      }
    } catch (error) {
      console.error("Error fetching form fields:", error);
    }
  };

  const formLoading = formFieldsAPI.loading;

  useEffect(() => {
    if (assignmentTemplateId) {
      panCardForm();
    }
  }, [assignmentTemplateId]);

  const handleFieldChange = (fieldId: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleNoDocumentChange = (fieldId: string, checked: boolean) => {
    setNoDocument((prev) => ({
      ...prev,
      [fieldId]: checked,
    }));

    if (checked) {
      handleFieldChange(fieldId, null);

      // Clear the file input so filename disappears
      if (fileInputRefs.current[fieldId]) {
        fileInputRefs.current[fieldId]!.value = "";
      }
    }
  };

  const handleSaveDocument = async () => {
    try {
      // Dynamic Validation
      const schema = generateSchema(formFields, noDocument);

      const validationResult = schema.safeParse(formData);

      if (!validationResult.success) {
        Swal.fire({
          icon: "error",
          title: "Validation Warning",
          text: validationResult.error.issues[0].message,
        });

        return;
      }

      const payload = new FormData();

      const jsonData: any = {};

      Object.keys(formData).forEach((key) => {
        const value = formData[key];

        if (value instanceof File) {
          payload.append(key, value);
        } else {
          jsonData[key] = value;
        }
      });

      payload.append("formData", JSON.stringify(jsonData));

      payload.forEach((value, key) => {
        console.log(key, value);
      });

      const response = await templateSaveAPI.callApi(payload);

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response?.message || "Document saved successfully",
          confirmButtonText: "OK",
        });
        dispatch(triggerPackageReload());
        panCardForm();
      }
    } catch (error: any) {
      console.error("Save Error:", error);

      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text:
          error?.response?.data?.error ||
          error?.message ||
          "Something went wrong",
      });
    }
  };

  const handleUpdateDocument = async () => {
    try {
      // Dynamic Validation
      const schema = generateSchema(formFields, noDocument);

      const validationResult = schema.safeParse(formData);

      if (!validationResult.success) {
        Swal.fire({
          icon: "error",
          title: "Validation Warning",
          text: validationResult.error.issues[0].message,
        });

        return;
      }

      const payload = new FormData();

      const jsonData: any = {};

      Object.keys(formData).forEach((key) => {
        const value = formData[key];

        if (value instanceof File) {
          payload.append(key, value);
        } else {
          jsonData[key] = value;
        }
      });

      payload.append("formData", JSON.stringify(jsonData));

      const response = await templateUpdateAPI.callApi(payload);

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response?.message || "Document update successfully",
          confirmButtonText: "OK",
        });
        // dispatch(triggerPackageReload());
        // panCardForm();
        // navigate(all_routes.caseSummary);
      }
    } catch (error: any) {
      console.error("Update Error:", error);

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error?.response?.data?.error ||
          error?.message ||
          "Something went wrong",
      });
    }
  };

  const handleReplyFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    setReplyDocuments(Array.from(e.target.files));
  };

  const removeReplyFile = (index: number) => {
    setReplyDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitReply = async () => {
    if (!replyRemarks.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Remarks Required",
        text: "Please enter your remarks before submitting.",
      });
      return;
    }

    try {
      const payload = new FormData();

      payload.append(
        "assignment_template_id",
        assignment_template_id ?? assignmentTemplateId ?? "",
      );

      payload.append("assignment_id", assignment_id ?? assignmentId ?? "");

      payload.append("case_id", case_id ?? caseId ?? "");

      payload.append("candidate_id", candidate_id ?? candidateId ?? "");

      payload.append("actor_due_on", actor_type ?? "CANDIDATE");
      payload.append("remarks", replyRemarks);

      // Multiple documents
      replyDocuments.forEach((file) => {
        payload.append("documents", file);
      });

      const res = await insuffResponse.callApi(payload);

      if (res?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Your response has been submitted successfully.",
        });

        // Reset form
        // setReplyRemarks("");
        // setReplyDocuments([]);
        // setShowReplyModal(false);
        navigate(all_routes.caseSummary);
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: error?.message || "Unable to submit your response.",
      });
    }
  };

  // ------------ Digi locker flow ----------

  const [digiLockerLoading, setDigiLockerLoading] = useState(false);

  const SUREPASS_API_KEY = REACT_APP_SUREPASS_API_KEY;

  const handleGetPanFromDigiLocker = async () => {
    try {
      setDigiLockerLoading(true);

      const response = await fetch(
        "https://kyc-api.surepass.app/api/v1/digilocker/initialize",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${SUREPASS_API_KEY}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            data: {
              signup_flow: true,
              // logo_url: `${window.location.origin}/logo.png`,
              redirect_url: window.location.href,
              skip_main_screen: false,
            },
          }),
        },
      );

      const result = await response.json();
      if (!result?.success || !result?.data?.url) {
        throw new Error(result?.message || "Unable to initialize DigiLocker.");
      }

      // Open DigiLocker
      window.open(result.data.url, "_blank", "noopener,noreferrer");
    } catch (error: any) {
      console.error("DigiLocker Error:", error);

      Swal.fire({
        icon: "error",
        title: "Unable to Open DigiLocker",
        text:
          error?.message || "Something went wrong while opening DigiLocker.",
      });
    } finally {
      setDigiLockerLoading(false);
    }
  };


  return (
    <>
      <div className="page-wrapper">
        {formLoading ? (
          <SnipperLoader />
        ) : (
          <div className="content">
            <div className="row">
              <div className="col-md-8">
                <form className="card overflow-hidden">
                  {/* Header */}
                  <div className="border-bottom px-4 py-3">
                    <h5 className="fw-semibold mb-0">PAN Card</h5>
                  </div>

                  <div className="p-4">
                    {/* Top Form */}
                    <div className="row mb-4">
                      {formFields
                        .filter((field) => field.type !== "file")
                        .sort((a, b) => a.display_order - b.display_order)
                        .map((field) => (
                          <div key={field.field_uuid} className="col-md-6 ">
                            <label className="form-label fw-medium">
                              {field.label}

                              {field.required && (
                                <span className="text-danger ms-1">*</span>
                              )}
                            </label>

                            <input
                              type={field.type}
                              className="form-control"
                              placeholder={field.placeholder}
                              value={formData[field.field_id] || ""}
                              required={field.required}
                              disabled={disabledStatuses.includes(
                                templateStatus ?? "",
                              )}
                              onChange={(e) =>
                                handleFieldChange(
                                  field.field_id,
                                  e.target.value,
                                )
                              }
                              pattern={field?.validation_rules?.pattern}
                              maxLength={field?.validation_rules?.maxLength}
                              minLength={field?.validation_rules?.minLength}
                            />
                          </div>
                        ))}
                    </div>
                    {/* Document Section */}
                    <div className="border rounded-3 p-4 bg-light">
                      <h4 className="fw-bold mb-4">PAN Card Documents</h4>

                      {/* Instructions */}
                      <div className="mb-4">
                        <h6 className="fw-semibold">Instructions:</h6>

                        <p className="mb-2">Please fill the form first</p>

                        <ol
                          className="ps-3 mb-0"
                          style={{
                            lineHeight: "28px",
                            fontSize: "14px",
                            listStyle: "number",
                          }}
                        >
                          <li>
                            Kindly ensure the photocopy/picture is readable, and
                            is not cut/pixelated/blurred.
                          </li>

                          <li>
                            Documents Accepted: Pdf, Jpeg, Jpg, Png, doc/docx
                            are allowed.
                          </li>

                          <li>
                            Please ensure document is not more than 5MB in size.
                          </li>
                        </ol>
                      </div>

                      {formFields
                        .filter((field) => field.type === "file")
                        .sort((a, b) => a.display_order - b.display_order)
                        .map((field) => (
                          <div
                            key={field.field_uuid}
                            className="row align-items-center mb-5"
                          >
                            <div className="col-lg-6">
                              <label className="form-label fw-semibold">
                                {field.label}
                              </label>

                              {field.required && (
                                <span className="text-danger ms-1">*</span>
                              )}
                              <input
                                ref={(el) => {
                                  fileInputRefs.current[field.field_id] = el;
                                }}
                                type={field.type}
                                className="form-control"
                                accept={field.file_config?.allowedTypes
                                  ?.map((type: string) => `.${type}`)
                                  .join(",")}
                                onChange={(e) =>
                                  handleFieldChange(
                                    field.field_id,
                                    e.target.files?.[0] || null,
                                  )
                                }
                                disabled={
                                  noDocument[field.field_id] ||
                                  disabledStatuses.includes(
                                    templateStatus ?? "",
                                  )
                                }
                                required={field?.required}
                              />
                              <div className="form-check mt-2">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={noDocument[field.field_id] || false}
                                  onChange={(e) =>
                                    handleNoDocumentChange(
                                      field.field_id,
                                      e.target.checked,
                                    )
                                  }
                                />

                                <label className="form-check-label text-muted small">
                                  I do not have the document
                                </label>
                              </div>
                            </div>

                            <div className="col-lg-6 text-center mt-4 mt-lg-0">
                              {field.uploaded_files?.length > 0 ? (
                                <a
                                  href={`${apibaseurl}${field.uploaded_files[0].url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-decoration-none"
                                >
                                  <i
                                    className="bi bi-file-earmark-pdf-fill text-danger"
                                    style={{
                                      fontSize: "50px",
                                      cursor: "pointer",
                                    }}
                                  />

                                  <p
                                    className="text-danger small"
                                    style={{ cursor: "pointer" }}
                                  >
                                    {formData[field.field_id]
                                      ? formData[field.field_id].name
                                      : field.uploaded_files?.[0]
                                          ?.original_name || "No File Selected"}
                                  </p>
                                </a>
                              ) : (
                                <>
                                  <i
                                    className="bi bi-file-earmark-pdf-fill text-danger"
                                    style={{ fontSize: "50px" }}
                                  />

                                  <p className="text-danger small">
                                    {formData[field.field_id]
                                      ? formData[field.field_id].name
                                      : "No File Selected"}
                                  </p>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                    {disabledStatuses.includes(templateStatus ?? "") ||
                    templateStatus === "CANDIDATE_INSUFF" ? null : (
                      <div className="d-flex gap-2 align-item-center mt-5">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSaveDocument}
                          disabled={templateSaveAPI.loading}
                        >
                          {templateSaveAPI.loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="ti ti-device-floppy me-2"></i>
                              Save
                            </>
                          )}
                        </button>

                        {/* <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={handleGetPanFromDigiLocker}
                          disabled={digiLockerLoading}
                        >
                          {digiLockerLoading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              />
                              Opening DigiLocker...
                            </>
                          ) : (
                            <>
                              <i className="ti ti-file-download me-2"></i>
                              Get PAN from DigiLocker
                            </>
                          )}
                        </button> */}
                      </div>
                    )}
                    {templateStatus === "CANDIDATE_INSUFF" && (
                      <div className="d-flex gap-2 align-items-center mt-5">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleUpdateDocument}
                        >
                          <i className="ti ti-upload me-2"></i>
                          Save Form & Documents
                        </button>

                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={() => setShowReplyModal(true)}
                        >
                          <i className="ti ti-message-circle me-2"></i>
                          Reply to Insufficiency
                        </button>
                      </div>
                    )}
                  </div>
                </form>
              </div>
              <div className="col-md-4">
                <div
                  style={{
                    position: "sticky",
                    top: "60px",
                    alignSelf: "flex-start",
                  }}
                >
                  <div className="card border-0 shadow-sm">
                    {/* Header */}
                    <div
                      className="text-center py-4"
                      style={{ backgroundColor: "#f7f1e3" }}
                    >
                      <div
                        className="d-flex align-items-center justify-content-center mx-auto mb-2 rounded-circle bg-white"
                        style={{ width: "70px", height: "70px" }}
                      >
                        {/* <i
                      className="bi bi-lightbulb-fill text-warning"
                      style={{ fontSize: "35px" }}
                    ></i> */}
                        <img src={bulbImage} alt="buld" />
                      </div>

                      <h6 className="fw-bold mb-0">Instructions</h6>
                    </div>

                    {/* Body */}
                    <div className="card-body">
                      {/* Instruction 1 */}
                      <div className="d-flex mb-4">
                        <div
                          className="rounded me-3 mt-1 flex-shrink-0"
                          style={{
                            width: "16px",
                            height: "16px",
                            backgroundColor: "#f4c542",
                          }}
                        ></div>

                        <div>
                          <h6 className="mb-1 fw-semibold">Instruction 1</h6>

                          <p className="text-muted mb-0 small">
                            <b>
                              Please provide your PAN Card details in the form.
                            </b>
                          </p>
                        </div>
                      </div>

                      {/* Instruction 2 */}
                      <div className="d-flex mb-4">
                        <div
                          className="rounded me-3 mt-1 flex-shrink-0"
                          style={{
                            width: "16px",
                            height: "16px",
                            backgroundColor: "#f4c542",
                          }}
                        ></div>
                        <div>
                          <h6 className="mb-1 fw-semibold">Instruction 2</h6>

                          <p className="text-muted mb-0 small">
                            You maybe required to attach a copy of the PAN Card.
                            Kindly ensure the photocopy/picture is readable, and
                            is not cut/pixelated/blurred.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {showReplyModal && (
              <div
                className="modal fade show d-block"
                style={{ background: "rgba(0,0,0,.5)" }}
              >
                <div className="modal-dialog modal-lg modal-dialog-centered">
                  <div className="modal-content border-0 shadow">
                    {/* Header */}
                    <div className="modal-header">
                      <div>
                        <h5 className="modal-title fw-bold mb-1">
                          Respond to Insufficiency
                        </h5>

                        <small className="text-muted">
                          Please provide your explanation and upload supporting
                          documents if applicable.
                        </small>
                      </div>

                      <button
                        className="btn-close"
                        onClick={() => setShowReplyModal(false)}
                      />
                    </div>

                    {/* Body */}
                    <div className="modal-body">
                      {/* Remarks */}
                      <div className="mb-4">
                        <label className="form-label fw-semibold">
                          Remarks
                          <span className="text-danger ms-1">*</span>
                        </label>

                        <textarea
                          rows={5}
                          className="form-control"
                          placeholder="Enter your response to the verifier..."
                          value={replyRemarks}
                          onChange={(e) => setReplyRemarks(e.target.value)}
                          style={{ resize: "none" }}
                        />

                        <small className="text-muted">
                          Clearly explain the requested information or
                          clarification.
                        </small>
                      </div>

                      {/* Upload */}
                      <div className="mb-3">
                        <label className="form-label fw-semibold">
                          Supporting Documents
                        </label>

                        <input
                          type="file"
                          className="form-control"
                          multiple
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={handleReplyFileChange}
                        />

                        <small className="text-muted">
                          Accepted formats: JPG, JPEG, PNG, PDF (Maximum 10 MB
                          per file)
                        </small>
                      </div>

                      {/* Selected Files */}
                      {replyDocuments.length > 0 && (
                        <div className="mt-3">
                          <label className="fw-semibold mb-2">
                            Selected Files
                          </label>

                          <div className="d-flex flex-wrap gap-2">
                            {replyDocuments.map((file, index) => (
                              <span
                                key={index}
                                className="badge rounded-pill bg-success-subtle text-dark border border-success-subtle px-3 py-2 d-flex align-items-center"
                              >
                                <i className="ti ti-paperclip me-2"></i>

                                <span
                                  className="text-truncate"
                                  style={{ maxWidth: 180 }}
                                >
                                  {file.name}
                                </span>

                                <button
                                  type="button"
                                  className="btn btn-sm border-0 bg-transparent text-danger ms-2 p-0"
                                  onClick={() => removeReplyFile(index)}
                                >
                                  <i className="ti ti-x"></i>
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="modal-footer gap-3">
                      <button
                        className="btn btn-light border"
                        onClick={() => setShowReplyModal(false)}
                      >
                        Cancel
                      </button>

                      <button
                        className="btn btn-primary"
                        onClick={handleSubmitReply}
                        disabled={insuffResponse.loading}
                      >
                        {insuffResponse.loading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <i className="ti ti-send me-2"></i>
                            Submit Response
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <Footer />
      </div>
    </>
  );
};

export default PanCardWithoutValid;
